import React from 'react';
import { btnSmall, input, label } from './styles';
import { fromRows, rowsError, toRows } from './variantRows';

const GST_RATES = [0, 5, 12, 18, 28];

/** Editable fields of a draft, as strings while editing (see toForm / fromForm). */
export const EDITABLE_FIELDS = [
  'name', 'description', 'brandName', 'categoryName', 'subcategoryName',
  'mrp', 'eyantraPurchasePrice', 'marketOperatingPrice', 'gst',
  'colors', 'sizes', 'dimensionL', 'dimensionW', 'dimensionH', 'dimensionUnit',
  'sellerName', 'sellerContactInformation', 'spoc', 'itemsAvailable',
];
const NUMERIC = new Set(['mrp', 'eyantraPurchasePrice', 'marketOperatingPrice', 'gst',
  'dimensionL', 'dimensionW', 'dimensionH', 'itemsAvailable']);

export const toForm = (item) => ({
  ...Object.fromEntries(EDITABLE_FIELDS.map((f) => [f, item[f] === null || item[f] === undefined ? '' : String(item[f])])),
  variants: toRows(item.variants),
});

/** Form strings → PUT body. Empty means "not set" (null), never 0. */
export const fromForm = (form) => ({
  ...Object.fromEntries(EDITABLE_FIELDS.map((f) => {
    const v = (form[f] ?? '').trim();
    if (v === '') return [f, null];
    return [f, NUMERIC.has(f) ? Number(v) : v];
  })),
  variants: fromRows(form.variants),
});

/** Returns the first field with an unparseable number, so Save can refuse instead of sending NaN. */
export const invalidNumberField = (form) =>
  [...NUMERIC].find((f) => (form[f] ?? '').trim() !== '' && !Number.isFinite(Number(form[f])));

/** A message for the first bad number anywhere in the form (fields or options), or null. */
export const formError = (form) => {
  const bad = invalidNumberField(form);
  if (bad) return `"${form[bad]}" isn't a number.`;
  return rowsError(form.variants);
};

function Field({ id, text, children, span = 1 }) {
  return (
    <div style={{ gridColumn: span === 'full' ? '1 / -1' : `span ${span}`, minWidth: 0 }}>
      <label style={label} htmlFor={id}>{text}</label>
      {children}
    </div>
  );
}

/**
 * The draft's form. Brand and category use free text with suggestions: the reviewer can pick an
 * existing value or keep a new one (a new brand needs explicit confirmation at create time).
 */
export default function DraftFields({ itemId, form, setField, disabled, brands, categories, suggestedGst }) {
  const id = (f) => `draft-${itemId}-${f}`;
  const text = (f, placeholder, type = 'text') => (
    <input id={id(f)} type={type} style={input} value={form[f]} disabled={disabled} placeholder={placeholder}
           inputMode={NUMERIC.has(f) ? 'decimal' : undefined}
           onChange={(e) => setField(f, e.target.value)} />
  );
  const category = categories.find((c) => c.name.toLowerCase() === form.categoryName.trim().toLowerCase());
  const subcategories = category?.subcategories || [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
      <Field id={id('name')} text="Product name *" span={2}>{text('name')}</Field>

      <Field id={id('brandName')} text="Brand *">
        <input id={id('brandName')} list={`brands-${itemId}`} style={input} value={form.brandName} disabled={disabled}
               onChange={(e) => setField('brandName', e.target.value)} />
        <datalist id={`brands-${itemId}`}>{brands.map((b) => <option key={b.id} value={b.name} />)}</datalist>
      </Field>

      <Field id={id('categoryName')} text="Category *">
        <input id={id('categoryName')} list={`cats-${itemId}`} style={input} value={form.categoryName} disabled={disabled}
               onChange={(e) => setField('categoryName', e.target.value)} />
        <datalist id={`cats-${itemId}`}>{categories.map((c) => <option key={c.id} value={c.name} />)}</datalist>
      </Field>

      <Field id={id('subcategoryName')} text="Subcategory *">
        <input id={id('subcategoryName')} list={`subcats-${itemId}`} style={input} value={form.subcategoryName} disabled={disabled}
               onChange={(e) => setField('subcategoryName', e.target.value)} />
        <datalist id={`subcats-${itemId}`}>{subcategories.map((s) => <option key={s.id} value={s.name} />)}</datalist>
      </Field>

      <Field id={id('mrp')} text="MRP (₹, incl. GST) *">{text('mrp', 'Not printed')}</Field>

      <Field id={id('gst')} text="GST % *">
        <select id={id('gst')} style={input} value={form.gst} disabled={disabled} onChange={(e) => setField('gst', e.target.value)}>
          <option value="">Not set</option>
          {GST_RATES.map((r) => <option key={r} value={String(r)}>{r}%</option>)}
        </select>
        {form.gst === '' && suggestedGst !== null && suggestedGst !== undefined && !disabled && (
          <button type="button" style={{ ...btnSmall, marginTop: 6 }} onClick={() => setField('gst', String(suggestedGst))}
                  title="Most common GST among existing products in this category — check it applies to this product">
            Use {suggestedGst}% (common in this category)
          </button>
        )}
      </Field>

      <Field id={id('eyantraPurchasePrice')} text="EPP (₹, excl. GST)">{text('eyantraPurchasePrice')}</Field>
      <Field id={id('marketOperatingPrice')} text="MOP (₹, excl. GST)">{text('marketOperatingPrice')}</Field>
      <Field id={id('itemsAvailable')} text="Available quantity">{text('itemsAvailable', '0')}</Field>

      <Field id={id('colors')} text="Colours (comma-separated)" span={2}>{text('colors')}</Field>
      <Field id={id('sizes')} text="Sizes (comma-separated)">{text('sizes')}</Field>

      <Field id={id('dimensionL')} text="Length">{text('dimensionL')}</Field>
      <Field id={id('dimensionW')} text="Width">{text('dimensionW')}</Field>
      <Field id={id('dimensionH')} text="Height">{text('dimensionH')}</Field>
      <Field id={id('dimensionUnit')} text="Unit">{text('dimensionUnit', 'cm')}</Field>

      <Field id={id('sellerName')} text="Seller">{text('sellerName')}</Field>
      <Field id={id('sellerContactInformation')} text="Seller contact">{text('sellerContactInformation')}</Field>
      <Field id={id('spoc')} text="SPOC">{text('spoc')}</Field>

      <Field id={id('description')} text="Description" span="full">
        <textarea id={id('description')} style={{ ...input, minHeight: 90, resize: 'vertical' }} value={form.description}
                  disabled={disabled} onChange={(e) => setField('description', e.target.value)} />
      </Field>
    </div>
  );
}
